import * as React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {clsx} from 'clsx';
import {Input} from '../../../components/ui/Input/Input';
import {CheckboxInput} from '../../../components/ui/Checkbox/CheckboxInput';
import {Button} from '../../../components/ui/shadcn-components/button';
import {SelectBox} from '../../../components/ui/SelectBox/SelectBox';
import {State} from '../../data/general/state.enum';
import {translate} from '../../../app/shared/utils/localization-utils';
import SubTitle from '../../../app/shared/layout/subtitle/subTitle';
import Title from '../../../app/shared/layout/title/title';
import {TextArea} from '../../../components/ui/TextArea/TextArea';
import {MultipleFileSelect} from '../../../components/ui/MultipleFileSelect/MultipleFileSelect';
import {MultipleTextSelect} from '../../../components/ui/MultipleTextSelect/MultipleTextSelect';
import MultiSelect from '../../../components/ui/MultiSelect/MultiSelect';
import {DatePicker} from '../../../components/ui/DatePicker/DatePicker';

interface IRegisterForm {
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const skillOptions = [
	{
		label: 'JavaScript',
		value: 'JavaScript'
	},
	{
		label: 'TypeScript',
		value: 'TypeScript'
	},
	{
		label: 'React',
		value: 'React'
	},
	{
		label: 'Node.js',
		value: 'Node.js'
	},
	{
		label: 'Python',
		value: 'Python'
	},
	{
		label: 'Java',
		value: 'Java'
	},
	{
		label: 'C#',
		value: 'C#'
	},
	{
		label: 'Go',
		value: 'Go'
	}
];

type SkillValue = (typeof skillOptions)[number]['value']

const registrationFormSchema = z.object({
	state: z.string({required_error: translate('error.required')}),
	firstName: z.string()
		.min(1, translate('error.required')),
	lastName: z.string()
		.min(1, translate('error.required')),
	note: z.string()
		.min(1, translate('error.required')),
	skills: z
		.array(z.string() as z.ZodType<SkillValue>)
		.min(1, translate('error.required')),
	files: z
		.array(z.instanceof(File))
		.nonempty(translate('error.requiredFiles')),
	gdpr: z.boolean({required_error: translate('error.required')}),
	emails: z
		.array(z.string().email(translate('error.invalidEmail')))
		.min(1, translate('error.required')),
	date: z.string().min(1, 'Dátum je povinný.'),

});

export type RegistrationFormData = z.infer<typeof registrationFormSchema>

export default function GenericForm({
	className,
}: IRegisterForm) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		formState: {errors}
	} = useForm<RegistrationFormData>({
		resolver: zodResolver(registrationFormSchema),
	});

	const onSubmit = (formData: RegistrationFormData) => {
		console.log(formData, 'formData');
		reset();
	};

	return (
		<div>
			<Title customClass="flex mt-10" keyValue={'registration.title'}/>
			<form noValidate className={clsx('flex flex-col', className)}
				  onSubmit={handleSubmit(onSubmit)}>
				<SubTitle customClass="" keyValue={'testingPage.subheading.form'}/>

				<div
					className="grid grid-cols-1 md:grid-cols-3 gap-[22px] md:gap-[32px]">
					<Input register={register('firstName')}
						   text={translate('registration.name')}
						   error={errors.firstName?.message} name="firstName"
						   isRequired={true}/>
					<Input register={register('lastName')}
						   text={translate('registration.lastName')}
						   error={errors.lastName?.message} name="lastName"
						   isRequired={true}/>
					<Controller
						name="date"
						control={control}
						render={({ field, fieldState }) => (
							<DatePicker
								text="Vyberte dátum"
								field={field}
								error={fieldState.error?.message}
								hasHours={true}
							/>
						)}
					/>
					<Controller
						control={control}
						name="emails"
						render={({ field }) => (
							<MultipleTextSelect
								name={field.name}
								placeholder='Input pro postupné zadávání hodnot'
								onChange={field.onChange}
								error={typeof errors.emails?.message === 'string' ? errors.emails.message : undefined}
								className="md:col-span-3"
							/>
						)}
					/>
					<Controller
						control={control}
						name="gdpr"
						render={({ field }) => (
							<CheckboxInput
								text="Caramels brownie cookie jelly-o sweet. Tart sugar plum liquorice candy powder shortbread."
								checked={field.value}
								onCheckedChange={field.onChange}
								error={errors.gdpr?.message}
							/>
						)}
					/>
					<Controller control={control}
								name="state"
								render={({field}) => (
									<SelectBox value={field.value}
											   onChange={field.onChange}
											   placeholder={translate('registration.state')}
											   error={errors.state?.message}
											   selectData={Object.values(State)}
											   isRequired={true}
									/>
								)}
					/>
					<TextArea className="md:col-span-3 "
							  error={errors.note?.message}
							  textAreaClass="" name="note"
							  text={translate('form.note')}
							  register={register('note')} isRequired={true}/>
					<Controller
						control={control}
						name="skills"
						render={({field}) => (
							<MultiSelect
								register={register('skills')}
								name={field.name}
								options={skillOptions}
								onChange={field.onChange}
								error={errors.skills?.message}
								placeholder={translate('form.selectbox')}
							/>
						)}
					/>
					<Controller
						control={control}
						name="files"
						defaultValue={undefined}
						render={({ field }) => (
							<MultipleFileSelect
								className='md:col-span-3'
								name={field.name}
								placeholder={translate('file.attachments')}
								onChange={field.onChange}
								error={typeof errors.files?.message === 'string' ? errors.files.message : undefined}
							/>
						)}
					/>
				</div>

				<div className="flex items-center justify-end gap-4 mt-8">
					<Button type="submit"
							variant="default">{translate('buttonSend')}</Button>
				</div>
			</form>
		</div>
	);
}
